import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import {
  IMPERSONATE_KEY,
  IMPERSONATE_ROLE_KEY,
  clearImpersonation,
} from '../lib/impersonationPreview';
import { useAuth } from './useAuth';

export interface MatterSummary {
  id: string;
  matter_id: string | null;
  visa_subclass: string;
  variant: string | null;
  /** Matter-level relationship status (R5): Married / De facto, staff-set.
   *  Null until staff confirm it. The 820/309 intake binds the locked
   *  relationship-status display (and its gates) to this value. */
  relationship_status: string | null;
  status: string;
  initial_payment_date: string | null;
  primary_contact_id: string;
  sponsor_contact_id: string | null;
}

export interface ContactSummary {
  id: string;
  given_names: string | null;
  family_name: string | null;
  email: string | null;
  phone: string | null;
  contact_type: string;
  staff_role: string | null;
}

export interface CaseManagerSummary extends ContactSummary {
  staff_role: string | null;
}

interface MatterContextValue {
  loading: boolean;
  error: string | null;
  matters: MatterSummary[];
  activeMatter: MatterSummary | null;
  contact: ContactSummary | null;
  caseManager: CaseManagerSummary | null;
  /** True when the current user is *really* staff (ignores impersonation). */
  actuallyStaff: boolean;
  /** True when the UI should render staff features. False when impersonating a client. */
  isStaff: boolean;
  /** True when the current user is an owner (business principal). Owner-only
      controls (e.g. the engagement-letter gate toggle) gate on this. */
  isOwner: boolean;
  /** When set, UI re-skins as if we were the client of this matter. RLS still grants staff access. */
  impersonatedMatterId: string | null;
  /** Which role on the matter we're impersonating. Defaults to 'applicant'. */
  impersonatedRole: 'applicant' | 'sponsor';
  /**
   * The contact id "as which" the user is currently acting. When impersonating,
   * resolves to the impersonated matter's primary_contact_id (applicant role)
   * or sponsor_contact_id (sponsor role). Otherwise resolves to the real
   * signed-in user's contact.id. Used by client-shaped flows (intake form,
   * documents) so they read/write the right rows under impersonation.
   */
  effectiveContactId: string | null;
  setImpersonating: (matterId: string | null, role?: 'applicant' | 'sponsor') => void;
  setActiveMatterId: (id: string) => void;
  refresh: () => Promise<void>;
}

const MatterContext = createContext<MatterContextValue | undefined>(undefined);

const ACTIVE_MATTER_KEY = 'adi_portal_active_matter_v1';

export function MatterProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [matters, setMatters] = useState<MatterSummary[]>([]);
  const [activeMatterId, setActiveMatterIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ACTIVE_MATTER_KEY);
    } catch {
      return null;
    }
  });
  const [contact, setContact] = useState<ContactSummary | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [caseManager, setCaseManager] = useState<CaseManagerSummary | null>(null);
  const [impersonatedMatterId, setImpersonatedMatterIdState] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(IMPERSONATE_KEY);
    } catch {
      return null;
    }
  });
  const [impersonatedRole, setImpersonatedRoleState] = useState<'applicant' | 'sponsor'>(() => {
    try {
      const v = sessionStorage.getItem(IMPERSONATE_ROLE_KEY);
      return v === 'sponsor' ? 'sponsor' : 'applicant';
    } catch {
      return 'applicant';
    }
  });

  // Depend on `user.id` rather than the user object so the callback is not
  // re-created on every Supabase auth-state-change tick (which fires several
  // times on initial mount with new session object references). Without this,
  // the bootstrap effect re-fires N times and we see 4× matters + 4× contacts
  // GETs on every page load.
  const userId = user?.id ?? null;

  const load = useCallback(async () => {
    if (!userId) {
      setMatters([]);
      setContact(null);
      setIsOwner(false);
      setCaseManager(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Resolve current contact row for the auth user.
      const { data: contactRow, error: contactErr } = await supabase
        .from('contacts')
        .select('id, given_names, family_name, email, phone, contact_type, staff_role')
        .eq('user_id', userId)
        .maybeSingle();
      if (contactErr) throw contactErr;
      setContact(contactRow ?? null);

      // A non-staff contact must never inherit a stale "view as client" flag.
      // Only staff can ever legitimately hold one, and holding one silently
      // makes every field inert with no banner to explain why. Clear it the
      // moment we know who this user actually is.
      if (contactRow && contactRow.contact_type !== 'staff') {
        clearImpersonation();
        setImpersonatedMatterIdState(null);
        setImpersonatedRoleState('applicant');
      }

      // Owner flag (separate query: is_owner isn't in the generated types yet).
      const { data: ownerRow } = await supabase
        .from('contacts' as never)
        .select('is_owner')
        .eq('user_id', userId)
        .maybeSingle();
      setIsOwner((ownerRow as { is_owner: boolean | null } | null)?.is_owner === true);

      // RLS scopes the matters query to the current user's matters.
      const { data: matterRows, error: mErr } = await supabase
        .from('matters')
        .select(
          'id, matter_id, visa_subclass, variant, relationship_status, status, initial_payment_date, assigned_staff_id, primary_contact_id, sponsor_contact_id',
        )
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (mErr) throw mErr;
      const rows = (matterRows ?? []) as Array<MatterSummary & { assigned_staff_id: string | null }>;
      setMatters(rows.map(({ assigned_staff_id: _ignored, ...rest }) => rest));

      // Pick a sensible default active matter.
      if (rows.length === 1) {
        setActiveMatterIdState(rows[0].id);
      } else if (rows.length > 1) {
        setActiveMatterIdState((prev) => {
          if (prev && rows.find((r) => r.id === prev)) return prev;
          return rows[0].id;
        });
      } else {
        setActiveMatterIdState(null);
      }

      // Look up the case manager for the active matter (if any).
      const active =
        rows.find((r) => r.id === activeMatterId) ?? rows[0] ?? null;
      if (active?.assigned_staff_id) {
        const { data: staff } = await supabase
          .from('contacts')
          .select('id, given_names, family_name, email, phone, contact_type, staff_role')
          .eq('id', active.assigned_staff_id)
          .maybeSingle();
        setCaseManager(staff ?? null);
      } else {
        setCaseManager(null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load matter';
      setError(message);
    } finally {
      setLoading(false);
    }
    // activeMatterId intentionally excluded from deps to avoid loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (activeMatterId) {
      try {
        localStorage.setItem(ACTIVE_MATTER_KEY, activeMatterId);
      } catch {
        /* ignore */
      }
    }
  }, [activeMatterId]);

  const setActiveMatterId = useCallback((id: string) => {
    setActiveMatterIdState(id);
  }, []);

  const setImpersonating = useCallback((matterId: string | null, role: 'applicant' | 'sponsor' = 'applicant') => {
    setImpersonatedMatterIdState(matterId);
    setImpersonatedRoleState(role);
    try {
      if (matterId) {
        sessionStorage.setItem(IMPERSONATE_KEY, matterId);
        sessionStorage.setItem(IMPERSONATE_ROLE_KEY, role);
      } else {
        sessionStorage.removeItem(IMPERSONATE_KEY);
        sessionStorage.removeItem(IMPERSONATE_ROLE_KEY);
      }
    } catch {
      /* ignore */
    }
    // When entering impersonation, also pin the active matter so client-shaped
    // routes (Intake, Documents, Messages) read the same matter the staff was
    // previewing.
    if (matterId) {
      setActiveMatterIdState(matterId);
    }
  }, []);

  const activeMatter = useMemo(
    () => matters.find((m) => m.id === activeMatterId) ?? null,
    [matters, activeMatterId],
  );

  const actuallyStaff = useMemo(() => contact?.contact_type === 'staff', [contact]);
  const isStaff = useMemo(
    () => actuallyStaff && !impersonatedMatterId,
    [actuallyStaff, impersonatedMatterId],
  );

  const effectiveContactId = useMemo<string | null>(() => {
    if (!impersonatedMatterId) return contact?.id ?? null;
    // Under impersonation, NEVER fall back to the staff contact — that binds
    // client-shaped flows (intake autosave, documents) to the staff user. Return
    // null instead: when the target matter has not loaded yet, consumers wait
    // (the intake bootstrap guards on !effectiveContactId); when the impersonated
    // role has no contact on the matter (e.g. sponsor role, no sponsor seeded),
    // consumers refuse rather than write under staff.
    const target = matters.find((m) => m.id === impersonatedMatterId);
    if (!target) return null; // matter not loaded yet — consumers wait
    return impersonatedRole === 'sponsor'
      ? target.sponsor_contact_id ?? null
      : target.primary_contact_id ?? null;
  }, [impersonatedMatterId, impersonatedRole, matters, contact?.id]);

  const value = useMemo<MatterContextValue>(
    () => ({
      loading,
      error,
      matters,
      activeMatter,
      contact,
      caseManager,
      actuallyStaff,
      isStaff,
      isOwner,
      impersonatedMatterId,
      impersonatedRole,
      effectiveContactId,
      setImpersonating,
      setActiveMatterId,
      refresh: load,
    }),
    [
      loading,
      error,
      matters,
      activeMatter,
      contact,
      caseManager,
      actuallyStaff,
      isStaff,
      isOwner,
      impersonatedMatterId,
      impersonatedRole,
      effectiveContactId,
      setImpersonating,
      setActiveMatterId,
      load,
    ],
  );

  return <MatterContext.Provider value={value}>{children}</MatterContext.Provider>;
}

export function useMatter(): MatterContextValue {
  const ctx = useContext(MatterContext);
  if (!ctx) {
    throw new Error('useMatter must be used inside <MatterProvider>');
  }
  return ctx;
}
